import { useNizza } from '@nizza/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import baseLogger from '~logger';
import type { Product } from '../types';

const logger = baseLogger.child('useGetCollection');

type Props = {
  collectionId: string;
};

export const useGetCollection = ({ collectionId }: Props) => {
  const productDataSource = useNizza(x => x.productDataSource);
  const [products, setProducts] = useState<Product[]>([]);
  const [name, setName] = useState('');

  const productListResult = useQuery({
    queryKey: ['getCollection', collectionId],
    queryFn: async () => {
      if (!productDataSource) {
        const err = new Error(
          'Product data source is undefined. Unable to fetch collection',
        );

        logger.fatal(err, { collectionId });
        throw err;
      }

      return productDataSource.getCollection({ collectionId });
    },
    enabled: !!collectionId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (
      productListResult.isLoading ||
      productListResult.isError ||
      !productListResult.data
    )
      return;

    const mappedList: Product[] = productListResult.data.products.map(
      product => ({
        id: product.id,
        name: product.name,
        brand: product.brand,
        sku: product.skuId,
        image: product.imageUrl,
        price: product.price,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        actions: () => {},
      }),
    );

    setName(productListResult.data.name);
    setProducts(mappedList);
  }, [
    productListResult.data,
    productListResult.isError,
    productListResult.isLoading,
  ]);

  return {
    name,
    products,
    loading: productListResult.isFetching,
  };
};
